import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { db } from '../firebase/config';
import {
  markAsRead,
  markAllAsRead,
} from '../firebase/notificationsService';
import { showBrowserNotification } from '../utils/browserNotifications';

const NotificationsContext = createContext(null);

/**
 * App-shell notifications store — one live listener shared by every NotificationBell.
 */
export function NotificationsProvider({ children, previewLimit = 8 }) {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const seenIdsRef = useRef(new Set());
  const primedRef = useRef(false);

  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) {
      setNotifications([]);
      setUnreadCount(0);
      seenIdsRef.current = new Set();
      primedRef.current = false;
      return undefined;
    }

    setLoading(true);
    primedRef.current = false;

    const notifQ = query(
      collection(db, 'notifications'),
      where('userId', '==', uid),
      orderBy('dateCreated', 'desc'),
      limit(previewLimit)
    );

    const unsubNotifs = onSnapshot(
      notifQ,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNotifications(list);
        setLoading(false);

        // Browser ping for newly arrived chat messages while tab is hidden
        if (!primedRef.current) {
          list.forEach((n) => seenIdsRef.current.add(n.id));
          primedRef.current = true;
          return;
        }

        list.forEach((n) => {
          if (seenIdsRef.current.has(n.id)) return;
          seenIdsRef.current.add(n.id);
          if (n.read) return;
          if (n.type !== 'chat_message' && n.type !== 'new_message') return;

          showBrowserNotification(n.title || 'New message', {
            body: n.message || '',
            tag: `chat-${n.data?.conversationId || n.id}`,
            onClick: () => {
              const url = n.actionUrl || '/messages';
              if (url.startsWith('/')) {
                window.location.assign(url);
              }
            },
          });
        });
      },
      (err) => {
        console.warn('Notifications listener failed:', err);
        setLoading(false);
      }
    );

    const unsubUser = onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        if (!snap.exists()) {
          setUnreadCount(0);
          return;
        }
        const d = snap.data() || {};
        const n = d.unreadNotificationCount ?? d.unreadNotifications;
        setUnreadCount(typeof n === 'number' && n > 0 ? n : 0);
      },
      () => {}
    );

    return () => {
      unsubNotifs();
      unsubUser();
    };
  }, [currentUser?.uid, previewLimit]);

  const refresh = useCallback(() => {
    // Live listeners keep state fresh; retained for API compatibility.
  }, []);

  const markRead = useCallback(
    async (ids) => {
      const uid = currentUser?.uid;
      if (!uid || !ids) return;
      const idList = Array.isArray(ids) ? ids : [ids];
      setNotifications((prev) =>
        prev.map((n) => (idList.includes(n.id) ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - idList.length));
      try {
        await markAsRead(uid, idList);
      } catch (_) {
        /* listener will resync */
      }
    },
    [currentUser?.uid]
  );

  const markAllRead = useCallback(async () => {
    const uid = currentUser?.uid;
    if (!uid) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllAsRead(uid);
    } catch (_) {
      /* listener will resync */
    }
  }, [currentUser?.uid]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      refresh,
      markRead,
      markAllRead,
    }),
    [notifications, unreadCount, loading, refresh, markRead, markAllRead]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  return useContext(NotificationsContext);
}
